// CancelFiscalInvoiceModal — Story 10.5 (E10).
// Modal de cancelamento de NF com textarea de motivo + double confirmation.

import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@control-center/ui';

import { useCancelFiscalInvoice } from '../../hooks/use-fiscal';
import { extractErrorMessage } from '../../lib/errors';
import { PillButton } from '../clients/PillButton';

export interface CancelFiscalInvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fiscalInvoiceId: string;
  invoiceNumber: string | null;
  onSuccess?: () => void;
}

export function CancelFiscalInvoiceModal({
  open,
  onOpenChange,
  fiscalInvoiceId,
  invoiceNumber,
  onSuccess,
}: CancelFiscalInvoiceModalProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [confirmStage, setConfirmStage] = useState<'form' | 'confirm'>('form');
  const cancel = useCancelFiscalInvoice();

  function handleClose(): void {
    setReason('');
    setConfirmStage('form');
    onOpenChange(false);
  }

  function handleProceed(): void {
    if (reason.trim().length < 10) return;
    setConfirmStage('confirm');
  }

  async function handleConfirm(): Promise<void> {
    try {
      await cancel.mutateAsync({ id: fiscalInvoiceId, reason: reason.trim() });
      toast.success('NF cancelada com sucesso.', {
        description: invoiceNumber ? `NF ${invoiceNumber}` : undefined,
      });
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error('Não conseguimos cancelar.', {
        description: extractErrorMessage(err, 'Tente de novo.'),
      });
      // Mantém modal aberto pra retry — volta pra form pra ajustar motivo se quiser
      setConfirmStage('form');
    }
  }

  const reasonTrimmed = reason.trim();
  const reasonLength = reasonTrimmed.length;
  const isValid = reasonLength >= 10 && reasonLength <= 500;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(520px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="flex items-center gap-2 text-[16px] font-semibold tracking-[-0.005em] text-ink">
              <AlertTriangle size={16} strokeWidth={2} className="text-bad" />
              {confirmStage === 'form' ? 'Cancelar NF' : 'Tem certeza?'}
              {invoiceNumber && (
                <span className="rounded-[6px] border border-line bg-panel-3 px-2 py-[2px] text-[11px] font-mono text-muted">
                  {invoiceNumber}
                </span>
              )}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          {confirmStage === 'form' ? (
            <>
              <Dialog.Description className="text-[13px] leading-relaxed text-muted">
                Esta ação <strong className="text-ink">não pode ser desfeita</strong>. A NF será
                cancelada na prefeitura via Asaas Notas.
              </Dialog.Description>

              <label className="mt-4 flex flex-col gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-3">
                  Motivo do cancelamento <span className="text-bad">*</span>
                </span>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="Ex: NF emitida com valor incorreto, cliente solicitou cancelamento, etc."
                  className={cn(
                    'w-full rounded-[10px] border bg-panel px-3 py-2 text-[13px] text-ink',
                    'placeholder:text-muted-3 outline-none transition-colors',
                    'focus:border-line-2 resize-y',
                    reasonLength > 0 && !isValid ? 'border-bad/70' : 'border-line',
                  )}
                />
                <div className="flex items-center justify-between text-[11px]">
                  <span
                    className={cn(
                      'text-muted-2',
                      reasonLength > 0 && reasonLength < 10 && 'text-bad',
                    )}
                  >
                    {reasonLength < 10 ? `Mínimo 10 caracteres (${reasonLength}/10)` : 'Mínimo OK'}
                  </span>
                  <span className="text-muted-3 tnum">{reasonLength}/500</span>
                </div>
              </label>

              <div className="mt-5 flex items-center justify-end gap-2">
                <PillButton type="button" onClick={handleClose}>
                  Voltar
                </PillButton>
                <PillButton type="button" danger disabled={!isValid} onClick={handleProceed}>
                  Cancelar NF
                </PillButton>
              </div>
            </>
          ) : (
            <>
              <Dialog.Description className="text-[13px] leading-relaxed text-muted">
                Confirma o cancelamento? Esta ação é{' '}
                <strong className="text-ink">irreversível</strong>. A NF{' '}
                {invoiceNumber && <strong className="text-ink">{invoiceNumber}</strong>} será
                cancelada na prefeitura.
              </Dialog.Description>

              <blockquote className="mt-3 border-l-2 border-line pl-3 text-[12px] italic text-muted-2">
                {reasonTrimmed}
              </blockquote>

              <div className="mt-5 flex items-center justify-end gap-2">
                <PillButton
                  type="button"
                  onClick={() => setConfirmStage('form')}
                  disabled={cancel.isPending}
                >
                  Editar motivo
                </PillButton>
                <PillButton
                  type="button"
                  danger
                  onClick={() => void handleConfirm()}
                  disabled={cancel.isPending}
                >
                  {cancel.isPending ? 'Cancelando…' : 'Confirmar cancelamento'}
                </PillButton>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
